import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { clientsApi } from '../api';
import { useCanEdit } from '../auth';
import { Plus, Search, Building2, ChevronRight } from 'lucide-react';

export function ClientsListPage() {
  const canEdit = useCanEdit();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['clients', { q: search, active: !showInactive ? true : undefined }],
    queryFn: () => clientsApi.list({ 
      q: search || undefined, 
      active: !showInactive ? true : undefined,
      limit: 100 
    }),
  });
  
  const clients = data?.data.data.clients || [];
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clienti</h1>
          <p className="text-gray-600">Gestisci l'anagrafica clienti</p>
        </div>
        {canEdit && (
          <Link to="/clients/new" className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Nuovo cliente
          </Link>
        )}
      </div>
      
      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca per nome o codice..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>Mostra disattivati</span>
          </label>
        </div>
      </div>
      
      {/* List */}
      <div className="card">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Caricamento...</div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">Errore nel caricamento</div>
        ) : clients.length === 0 ? (
          <div className="p-8 text-center">
            <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Nessun cliente trovato</p>
            {canEdit && (
              <Link to="/clients/new" className="btn-primary mt-4 inline-flex">
                <Plus className="w-4 h-4 mr-2" />
                Crea il primo cliente
              </Link>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {clients.map((client) => (
              <li key={client.id}>
                <Link
                  to={`/clients/${client.id}`}
                  className="flex items-center px-6 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                    <Building2 className="w-5 h-5 text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {client.name}
                      {!client.active && (
                        <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          Disattivato
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {client.code}
                      {client.contact?.referent && (
                        <span> • {client.contact.referent}</span>
                      )}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
